import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { colors } from '../../../shared/styles/colors';
import { bookingApi, Slot, Booking } from '../../../shared/api/booking-api';
import { AxiosError } from 'axios';
import { userStore } from '../../../entities/user/model/user-store';

const STATUS_STYLES: Record<string, string> = {
    pending_confirmation: 'bg-amber-100 text-amber-800',
    awaiting_payment: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700',
    cancelled: 'bg-gray-200 text-gray-700',
};

const STATUS_LABELS: Record<string, string> = {
    pending_confirmation: 'Очікує підтвердження',
    awaiting_payment: 'Очікує оплати',
    paid: 'Оплачено',
    cancelled: 'Скасовано',
};

const BOOKING_TYPE_LABELS: Record<string, string> = {
    online: 'Онлайн консультація',
    offline: 'Офлайн консультація',
};

const getKyivToday = () =>
    new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Kyiv',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date());

// === Компонент списка ===
const MyConsultationsList = observer(() => {
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const userEmail = userStore.user?.email?.trim() || '';
    const isProfileLoading = userStore.loading || userStore.profileStatus === 'pending';

    useEffect(() => {
        if (!userEmail) {
            setBookings([]);
            setError(null);
            setLoading(false);
            return;
        }

        const fetchBookings = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await bookingApi.getMyBookings(userEmail);
                setBookings(data);
            } catch (err) {
                console.error(err);
                setError("Не вдалося завантажити консультації");
            } finally {
                setLoading(false);
            }
        };

        fetchBookings();
    }, [userEmail]);

    if (!userEmail && isProfileLoading) {
        return <div className="text-center py-8 text-gray-500">Завантаження профілю...</div>;
    }

    if (!userEmail) {
        return (
            <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 text-center text-amber-800">
                Не вдалося визначити email користувача. Оновіть сторінку або перевірте дані профілю.
            </div>
        );
    }

    if (loading) {
        return <div className="text-center py-8 text-gray-500">Завантаження консультацій...</div>;
    }

    if (error) {
        return (
             <div className="bg-red-50 p-4 rounded-xl border border-red-100 text-center text-red-700">
                {error}
            </div>
        );
    }

    if (bookings.length === 0) {
        return (
            <div className="space-y-4">
                <div className="bg-white rounded-xl p-8 text-center border border-gray-100 shadow-sm">
                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Немає запланованих консультацій</h3>
                    <p className="text-gray-500 mb-6">Ви ще не записались на жодну консультацію.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {bookings.map((booking) => (
                <div key={booking.id} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                     <div>
                        <div className="text-sm text-blue-600 font-semibold mb-1 uppercase tracking-wide">
                            {BOOKING_TYPE_LABELS[booking.booking_type] || booking.booking_type}
                        </div>
                        <h4 className="text-xl font-bold text-gray-900 mb-2">
                            {new Date(booking.start_time).toLocaleDateString('uk-UA', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </h4>
                        {booking.consultant_name && (
                            <p className="text-gray-600">Консультант: {booking.consultant_name}</p>
                        )}
                     </div>
                     <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_STYLES[booking.status] || 'bg-gray-100 text-gray-700'}`}>
                            {STATUS_LABELS[booking.status] || booking.status_display || booking.status}
                        </span>
                     </div>
                </div>
            ))}
        </div>
    );
});

// Модалка удалена - переход на оплату сразу при клике на слот

// === Компонент календаря для записи ===
const BookingInterface = observer(() => {
    // Состояния
    const [selectedDate, setSelectedDate] = useState<string>(getKyivToday());
    const [slots, setSlots] = useState<Slot[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [processingSlot, setProcessingSlot] = useState<string | null>(null);
    const userEmail = userStore.user?.email?.trim() || '';
    const userPhone = userStore.user?.phone_number?.trim() || '';
    const isProfileLoading = userStore.loading || userStore.profileStatus === 'pending';
    const bookingBlockedMessage = isProfileLoading
        ? 'Профіль ще завантажується. Спробуйте за кілька секунд.'
        : !userEmail
            ? 'Не вдалося визначити email користувача. Оновіть сторінку.'
            : !userPhone
                ? 'Щоб записатися на консультацію, додайте номер телефону у профілі.'
                : null;
    const canCreateBooking = !bookingBlockedMessage;

    // Загрузка слотов при смене даты
    useEffect(() => {
        if (!selectedDate) return;

        const fetchSlots = async () => {
            setLoading(true);
            setError(null);
            setSlots([]);
            try {
                const data = await bookingApi.getSlots(selectedDate);
                setSlots(data.slots || []);
            } catch (err) {
                console.error(err);
                setError('Не вдалося завантажити слоти. Перевірте з\'єднання.');
            } finally {
                setLoading(false);
            }
        };

        fetchSlots();
    }, [selectedDate]);

    const handleSlotClick = async (slot: Slot) => {
        setError(null);
        setSuccessMsg(null);
        setProcessingSlot(slot.start);

        try {
            if (!canCreateBooking) {
                setError(bookingBlockedMessage);
                return;
            }

            // Формируем полную дату-время: "2026-02-12T09:00:00"
            const fullDateTime = `${selectedDate}T${slot.start}:00`;

            const bookingResponse = await bookingApi.createBooking({
                start_time: fullDateTime,
                client_email: userEmail,
                client_phone: userPhone,
                booking_type: "online"
            });

            const booking = bookingResponse.data;

            if (!booking || !booking.id) {
                 throw new Error("Не вдалося створити бронювання");
            }

            setSuccessMsg(
                'Час зарезервовано. Після підтвердження адміністратором ви отримаєте лист із посиланням на оплату.'
            );
            setSlots((prevSlots) => prevSlots.filter((item) => item.start !== slot.start));

        } catch (err) {
            const axiosErr = err as AxiosError<{ error?: string; [key: string]: unknown }>;
            const data = axiosErr.response?.data;
            let message = 'Помилка при записі або оплаті';
            if (data) {
                if (typeof data.error === 'string') message = data.error;
                else if (typeof data.detail === 'string') message = data.detail;
                else if (data.start_time && Array.isArray(data.start_time)) message = data.start_time[0];
                else if (data.client_email && Array.isArray(data.client_email)) message = data.client_email[0];
            }
            if (axiosErr.response?.status === 409) message = message || 'На це вже записано. Оберіть інший час.';
            setError(message);
        } finally {
            setProcessingSlot(null);
        }
    };

    return (
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
            <h3 className="text-xl font-semibold mb-6">Оберіть зручний час</h3>

            {bookingBlockedMessage && (
                <div className="mb-4 p-4 bg-amber-50 text-amber-800 rounded-lg border border-amber-100">
                    {bookingBlockedMessage}
                </div>
            )}

            {/* Выбор даты */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Дата консультації</label>
                <input
                    type="date"
                    value={selectedDate}
                    min={getKyivToday()}
                    onChange={(e) => {
                        setSelectedDate(e.target.value);
                        setSuccessMsg(null);
                    }}
                    className="block w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
            </div>

            {/* Сообщения об успехе/ошибке */}
            {error && (
                <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
                    {error}
                </div>
            )}
            {successMsg && (
                <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg">
                    {successMsg}
                </div>
            )}

            {/* Сетка слотов */}
            {loading ? (
                <div className="text-center py-8 text-gray-500">Завантаження доступних годин...</div>
            ) : slots.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {slots.map((slot) => {
                        const isProcessing = processingSlot === slot.start;
                        return (
                            <button
                                key={slot.start}
                                onClick={() => handleSlotClick(slot)}
                                disabled={isProcessing || !canCreateBooking}
                                className="flex flex-col items-center justify-center p-4 border border-blue-100 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all group relative disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="text-lg font-bold text-gray-800 group-hover:text-blue-700">
                                    {slot.start}
                                </span>
                                <span className="text-xs text-gray-400 mb-2">до {slot.end}</span>

                                {/* Вывод стоимости */}
                                <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full group-hover:bg-blue-100 transition-colors">
                                    {isProcessing ? 'Обробка...' : `${Math.round(slot.price)} ₴`}
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-gray-500">На цю дату немає вільних слотів</p>
                </div>
            )}
        </div>
    );
});

export const ConsultationsPage = () => {
    const [activeTab, setActiveTab] = useState<'list' | 'book'>('list');
    const [searchParams, setSearchParams] = useSearchParams();
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        if (searchParams.get('status') === 'success') {
            setShowSuccess(true);
            setActiveTab('list');

            // Clean up the URL so that reloading the page doesn't show success again
            // and so that users can bookmark the page without status=success
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('status');
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    return (
        <div className="w-full max-w-[1200px] mx-auto px-4 py-8">
            {showSuccess && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl flex items-center gap-3">
                    <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <div>
                        <p className="font-medium">Оплата пройшла успішно.</p>
                        <p className="text-sm">Статус консультації оновлено у списку ваших записів.</p>
                    </div>
                    <button
                        onClick={() => setShowSuccess(false)}
                        className="ml-auto text-green-700 hover:text-green-900"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            <div className="flex items-center gap-3 mb-8">
                <h2 className="text-3xl font-bold text-gray-900">
                    Консультації
                </h2>
                <Link 
                    to="/pages/consultation-info" 
                    className="text-blue-900 hover:text-blue-700 transition-colors"
                    title="Детальніше про консультації"
                >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </Link>
            </div>

            <div className="flex border-b border-gray-200 mb-8">
                <button
                    onClick={() => setActiveTab('list')}
                    className={`pb-4 px-6 font-medium text-lg transition-colors relative ${
                        activeTab === 'list' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Мої консультації
                    {activeTab === 'list' && (
                        <span className="absolute bottom-0 left-0 w-full h-0.5" style={{ backgroundColor: colors.accentPrimary || '#3B82F6' }} />
                    )}
                </button>

                <button
                    onClick={() => setActiveTab('book')}
                    className={`pb-4 px-6 font-medium text-lg transition-colors relative ${
                        activeTab === 'book' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Записатися
                    {activeTab === 'book' && (
                        <span className="absolute bottom-0 left-0 w-full h-0.5" style={{ backgroundColor: colors.accentPrimary || '#3B82F6' }} />
                    )}
                </button>
            </div>

            <div className="min-h-[400px]">
                {activeTab === 'list' ? <MyConsultationsList /> : <BookingInterface />}
            </div>
        </div>
    );
};
